import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ClassesEntity } from '../../../../../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../../../injection-tokens/entity-service-tokens';
import { ExportService } from '../../../../../../../../services/export.service';
import { ConfirmDialogService } from '../../../../../../../../services/confirm-dialog.service';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../../../../../../shared/pagination-options';

@Component({
  selector: 'app-templates-sub-tab',
  templateUrl: './templates-sub-tab.component.html',
  styleUrls: ['./templates-sub-tab.component.css'],
})
export class TemplatesSubTabComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() classId!: string;
  @Input() classEntity!: ClassesEntity;
  @Input() canDestroyResources = false;
  @Input() templateVms: any[] = [];
  @Input() isLoading = false;
  @Input() loadError: string | null = null;
  @Input() exportService!: ExportService;
  /** powerState dict from parent: id → 'PowerState/running' | 'PowerState/deallocated' | etc. */
  @Input() vmPowerStates: Record<string, string> = {};

  @Output() refreshRequested = new EventEmitter<void>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /**
   * Universal column set used for the header row.
   * Cell content switches on row.templateType ('vm' | 'acc').
   * vmOrCloud  → VM Name (vm) / Cloud Username (acc)
   * snapshotOrDesc → Snapshot (vm) / Description (acc)
   * ipAddress  → IP Address (vm) / — (acc)
   */
  readonly displayedColumns = [
    'templateName', 'type', 'subscriptionId', 'resourceGroup',
    'vmOrCloud', 'snapshotOrDesc', 'ipAddress', 'powerState', 'actions',
  ];
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;

  dataSource = new MatTableDataSource<any>([]);

  /** Tracks which template id has an in-flight action (disables that row's menu). */
  busyIds = new Set<string>();

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly confirmDialog: ConfirmDialogService,
    private readonly snackBar: MatSnackBar,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['templateVms']) {
      this.dataSource.data = this.templateVms ?? [];
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  isVmType(row: any): boolean {
    return (row?.templateType ?? '').toLowerCase() === 'vm';
  }

  isAccType(row: any): boolean {
    return (row?.templateType ?? '').toLowerCase() === 'acc';
  }

  isBusy(row: any): boolean {
    return this.busyIds.has(row.id);
  }

  isNotCreated(row: any): boolean {
    const s = row.customerStatus ?? null;
    return !s || s === 'draft';
  }

  getPowerState(row: any): string {
    return this.vmPowerStates[row.id] ?? row.powerState ?? row.customerStatus ?? 'unknown';
  }

  powerStateLabel(row: any): string {
    const s = this.getPowerState(row);
    if (s.includes('running')) return 'Running';
    if (s.includes('deallocated')) return 'Deallocated';
    if (s.includes('stopped')) return 'Stopped';
    if (s.includes('starting')) return 'Starting…';
    if (s.includes('stopping')) return 'Stopping…';
    if (s.includes('deallocating')) return 'Deallocating…';
    if (s === 'completed') return 'Snapshotted';
    if (s === 'snapshotting') return 'Snapshotting…';
    if (s === 'creating') return 'Provisioning…';
    if (s === 'draft') return 'Not created';
    if (s === 'snapshot_failed') return 'Snap failed';
    return '—';
  }

  powerStateClass(row: any): string {
    const s = this.getPowerState(row);
    if (s.includes('running') || s === 'completed') return 'ps-running';
    if (s.includes('deallocated') || s.includes('stopped')) return 'ps-stopped';
    if (s.includes('starting') || s.includes('stopping') || s.includes('deallocating')
      || s === 'snapshotting' || s === 'creating') return 'ps-transitioning';
    return 'ps-unknown';
  }

  truncate(val: string | null | undefined, len = 20): string {
    if (!val) return '—';
    return val.length > len ? val.slice(0, len) + '…' : val;
  }

  private todayStr(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private notify(msg: string, type: 'success' | 'error' | 'info' = 'success'): void {
    this.snackBar.open(msg, 'Dismiss', {
      duration: 4000,
      panelClass: type === 'error' ? ['snack-error'] : type === 'info' ? ['snack-info'] : [],
    });
  }

  // ---------------------------------------------------------------------------
  // CSV Export
  // ---------------------------------------------------------------------------

  exportCsv(): void {
    const rows = this.dataSource.data.map(t => ({
      'Template Name': t.templateName ?? '',
      'Type': t.templateType ?? '',
      'Subscription': t.subscriptionId ?? '',
      'Resource Group': t.resourceGroup ?? '',
      'VM Name / Cloud Username': this.isVmType(t) ? (t.vmName ?? '') : (t.cloudUsername ?? ''),
      'Snapshot / Description': this.isVmType(t) ? (t.snapshotName ?? '') : (t.description ?? ''),
      'IP Address': this.isVmType(t) ? (t.publicIPAddress ?? '') : '',
    }));
    this.exportService.toCsv(rows, `${this.classId}_templates_${this.todayStr()}`);
  }

  // ---------------------------------------------------------------------------
  // Per-row power actions (VM type only)
  // ---------------------------------------------------------------------------

  startTemplate(row: any): void {
    const vmLabel = row.vmName ?? row.templateName;
    this.confirmDialog.typedConfirm({
      title: `Start Template VM — ${row.templateName}`,
      message: `Start template VM "${vmLabel}". Azure compute charges will resume while the VM is running.`,
      type: 'info',
      confirmPhrase: 'START',
      confirmButtonText: 'Start VM',
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      this.busyIds.add(row.id);
      this.classService.startClassTemplateVm(row.id, this.classId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.busyIds.delete(row.id); this.notify(`Template VM "${vmLabel}" is starting.`); this.refreshRequested.emit(); },
          error: (err: any) => { this.busyIds.delete(row.id); this.notify(err?.error?.message ?? `Failed to start template VM "${vmLabel}".`, 'error'); },
        });
    });
  }

  shutdownTemplate(row: any): void {
    const vmLabel = row.vmName ?? row.templateName;
    this.confirmDialog.typedConfirm({
      title: `Shutdown Template VM — ${row.templateName}`,
      message: `Gracefully shuts down the guest OS on template VM "${vmLabel}". The VM stays allocated — Azure charges continue.`,
      type: 'warning',
      confirmPhrase: 'SHUTDOWN',
      confirmButtonText: 'Shutdown VM',
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      this.busyIds.add(row.id);
      this.classService.shutdownClassTemplateVm(row.id, this.classId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.busyIds.delete(row.id); this.notify(`Template VM "${vmLabel}" is shutting down.`); this.refreshRequested.emit(); },
          error: (err: any) => { this.busyIds.delete(row.id); this.notify(err?.error?.message ?? `Failed to shutdown template VM "${vmLabel}".`, 'error'); },
        });
    });
  }

  deallocateTemplate(row: any): void {
    const vmLabel = row.vmName ?? row.templateName;
    this.confirmDialog.typedConfirm({
      title: `Deallocate Template VM — ${row.templateName}`,
      message: `Stops and deallocates template VM "${vmLabel}" without a guest OS signal. Azure compute charges pause; storage charges continue.`,
      type: 'warning',
      confirmPhrase: 'DEALLOCATE',
      confirmButtonText: 'Deallocate VM',
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      this.busyIds.add(row.id);
      this.classService.deallocateClassTemplateVm(row.id, this.classId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.busyIds.delete(row.id); this.notify(`Template VM "${vmLabel}" is being deallocated.`); this.refreshRequested.emit(); },
          error: (err: any) => { this.busyIds.delete(row.id); this.notify(err?.error?.message ?? `Failed to deallocate template VM "${vmLabel}".`, 'error'); },
        });
    });
  }

  // ---------------------------------------------------------------------------
  // Destroy actions (gated by canDestroyResources)
  // ---------------------------------------------------------------------------

  destroyTemplateVm(row: any): void {
    const vmLabel = row.vmName ?? row.templateName;
    // Class-template rows expose `snapshotName` for the OS disk reference;
    // fall back to osDiskName / osDiskId for rows that carry those fields instead.
    const diskName = row.snapshotName ?? row.osDiskName ?? row.osDiskId ?? null;
    this.confirmDialog.typedConfirm({
      title: `Destroy Template VM — ${row.templateName}`,
      message: `Permanently destroys template VM "${vmLabel}". ` +
        `This also removes the associated NIC, Public IP, and NSG entries. ` +
        `The OS disk snapshot is NOT removed. This action cannot be undone. ` +
        `Note: this template may be shared with other classes under the same tenant — ` +
        `if any of them still have active User VMs, the server will block this action.`,
      details: [
        `Template: ${row.templateName ?? '—'}`,
        ...(vmLabel !== row.templateName ? [`VM Name: ${vmLabel}`] : []),
        ...(row.subscriptionId ? [`Subscription: ${row.subscriptionId}`] : []),
        ...(row.resourceGroup ? [`Resource Group: ${row.resourceGroup}`] : []),
        ...(diskName ? [`OS Snapshot: ${diskName}`] : []),
      ],
      type: 'error',
      confirmPhrase: 'DESTROY TEMPLATE VM',
      confirmSummary: diskName
        ? `Template VM only — OS Snapshot "${diskName}" will be RETAINED.`
        : 'Template VM will be deleted. The OS snapshot (if any) will be retained.',
      confirmButtonText: 'Destroy Template VM',
      secondaryOption: {
        phrase: 'DESTROY TEMPLATE VM WITH DISK',
        buttonText: 'Destroy Template VM + OS Disk',
        confirmSummary: diskName
          ? `Template VM AND OS Snapshot "${diskName}" will be PERMANENTLY DELETED.`
          : 'Template VM and all associated disk snapshots will be permanently deleted.',
        buttonColorClass: 'btn-danger-red',
      },
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      const withDisk = result.selectedOption === 'secondary';
      this.busyIds.add(row.id);
      this.classService.destroyTemplate(row.id, this.classId, withDisk)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.busyIds.delete(row.id); this.notify(`Template VM "${vmLabel}" destroyed.`); this.refreshRequested.emit(); },
          error: (err: any) => { this.busyIds.delete(row.id); this.notify(err?.error?.message ?? `Failed to destroy template VM "${vmLabel}".`, 'error'); },
        });
    });
  }

  deleteAccTemplate(row: any): void {
    this.confirmDialog.typedConfirm({
      title: `Delete Account Template — ${row.templateName}`,
      message: `Permanently deletes the account template "${row.templateName}" and its associated Azure AD resources. ` +
        `This action cannot be undone.`,
      details: [
        `Template: ${row.templateName ?? '—'}`,
        `Cloud Username: ${row.cloudUsername ?? '—'}`,
      ],
      type: 'error',
      confirmPhrase: 'DELETE',
      confirmButtonText: 'Delete Account Template',
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      this.busyIds.add(row.id);
      this.classService.destroyTemplateUserAccount(row.id, this.classId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.busyIds.delete(row.id); this.notify(`Account template "${row.templateName}" deleted.`); this.refreshRequested.emit(); },
          error: (err: any) => { this.busyIds.delete(row.id); this.notify(err?.error?.message ?? `Failed to delete account template "${row.templateName}".`, 'error'); },
        });
    });
  }
}
